type Environment = "eu" | "us" | "au";
type Stage = "dev" | "lab" | "verilab" | "test" | "prod";

interface EnvironmentRule {
  pattern: string;
  env: Environment;
  stage: Stage;
}

const WWW_ENV_MAPPING: Record<string, Environment> = {
  "1": "eu",
  "2": "eu",
  "3": "us",
  "4": "au",
  "5": "eu",
};

const createTomraConnectPattern = (
  wwwNumber: string,
  stage: Stage,
): EnvironmentRule => ({
  pattern: `www${wwwNumber}${stage === "prod" ? "" : `-${stage}`}.tomraconnect.com`,
  env: WWW_ENV_MAPPING[wwwNumber],
  stage,
});

const createTomraCloudPattern = (
  env: Environment,
  stage: Stage,
): EnvironmentRule => ({
  pattern: `*.${env}${stage === "prod" ? "-prod" : `.${stage}`}.tomra.cloud`,
  env,
  stage,
});

const ENVIRONMENT_RULES: EnvironmentRule[] = [
  // Local development
  { pattern: "localhost", env: "eu", stage: "dev" },
  { pattern: "localhost:*", env: "eu", stage: "dev" },

  // Special domains
  { pattern: "devtest.tomraconnect.com", env: "eu", stage: "lab" },
  { pattern: "verilab.tomraconnect.com", env: "eu", stage: "verilab" },

  // Tomra Connect domains with www prefixes
  ...Object.keys(WWW_ENV_MAPPING).flatMap((wwwNumber) => [
    createTomraConnectPattern(wwwNumber, "test"),
    createTomraConnectPattern(wwwNumber, "prod"),
  ]),

  // Tomra Cloud domains ({env}.{stage}.*.tomra.cloud pattern)
  ...(["eu", "us", "au"] as Environment[]).flatMap((env) =>
    (["lab", "test", "prod"] as Stage[]).map((stage) => ({
      pattern: `${env}.${stage}.*.tomra.cloud`,
      env,
      stage,
    })),
  ),

  // Tomra Cloud domains (*.{env}.{stage}.tomra.cloud and *.{env}-prod.tomra.cloud patterns)
  { pattern: "*.eu.lab.tomra.cloud", env: "eu", stage: "lab" },
  { pattern: "*.verilab.tomra.cloud", env: "eu", stage: "verilab" },
  ...(["eu", "us", "au"] as Environment[]).flatMap((env) => [
    createTomraCloudPattern(env, "test"),
    createTomraCloudPattern(env, "prod"),
  ]),
];

const matchesPattern = (host: string, pattern: string): boolean => {
  const regexPattern = pattern
    .replace(/\./g, "\\.")
    .replace(/\*/g, "[\\w-]+")
    .replace(/\?/g, ".");

  return new RegExp(`^${regexPattern}$`, "i").test(host);
};

export const getEnvironmentFromHost = (
  host: string,
): [string, Environment, Stage] => {
  const rule = ENVIRONMENT_RULES.find((rule) =>
    matchesPattern(host, rule.pattern),
  );

  return rule ? ["", rule.env, rule.stage] : ["", "eu", "test"];
};

const [, env, stage] = getEnvironmentFromHost(window.location.host);

const apiHost = () => {
  if (stage === "dev") {
    return "http://localhost:4567/import-installations-api";
  }
  if (stage === "verilab") {
    return "https://api.verilab.tomra.cloud/import-installations-api";
  }
  return `https://api.${env}.${stage}.tomra.cloud/import-installations-api`;
};

const keycloakHost = () => {
  if (stage === "dev") {
    return "https://id.eu.test.tomra.cloud/auth";
  }
  if (stage === "verilab") {
    return "https://id.verilab.tomra.cloud/auth";
  }
  return `https://id.${env}.${stage}.tomra.cloud/auth`;
};

const API_HOST =
  import.meta.env.VITE_API_HOST ||
  window.__ENVIRONMENT__?.API_HOST ||
  apiHost();
const KEYCLOAK_HOST = window.__ENVIRONMENT__?.KEYCLOAK_HOST || keycloakHost();

export { stage, API_HOST, KEYCLOAK_HOST };
